/**
 * OrderHistoryPage - Redesigned with Quiz styling
 */

import React, { useMemo } from "react";
import { Box, Divider, Stack, Typography, Chip } from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";

import { fmtDate } from "../utils/format";
import { QuizStyledCard } from "../components/shared/QuizStyledCard";
import { PageTitle } from "../components/shared/PageTitle";
import { QuizButton } from "../components/shared/QuizButton";
import { extractLineItemsWithImages, groupLineItems } from "../utils/lineItems";
import { QUIZ } from "@/theme/quizTokens";

// -----------------------------
// Types
// -----------------------------
type NoteAttr = { name?: string; value?: string };

type OrderLike = {
  id?: string | number;

  // Shopify-ish
  order_number?: string | number;
  shopify_order_number?: string | number;
  shopify_id?: string | number;
  shopify_order_id?: string | number;

  created_at?: string;
  processed_at?: string;
  scheduled_at?: string;

  status?: string; // e.g. "SUCCESS"
  state?: string;
  financial_status?: string;

  charge_status?: string; // e.g. "REFUNDED"
  type?: string; // e.g. "RECURRING"

  total_price?: string | number;
  total?: string | number;
  price?: string | number;
  currency?: string;

  total_discounts?: string | number;
  total_tax?: string | number;

  line_items?: any[];
  note_attributes?: NoteAttr[];
};

function isOrderObject(x: any): x is OrderLike {
  return (
    !!x &&
    typeof x === "object" &&
    ("id" in x || "charge_id" in x || "shopify_order_id" in x)
  );
}

// -----------------------------
// Normalize: array / nested / single object
// -----------------------------
function normalizeOrders(ordersPayload: any): OrderLike[] {
  if (!ordersPayload) return [];

  // 1) direct array
  if (Array.isArray(ordersPayload))
    return sortNewestFirst(ordersPayload.filter(isOrderObject));

  // 2) known wrappers
  const candidates = [
    ordersPayload?.orders,
    ordersPayload?.data?.orders,
    ordersPayload?.data,
    ordersPayload?.orders?.data,
  ];

  for (const c of candidates) {
    if (Array.isArray(c)) return sortNewestFirst(c.filter(isOrderObject));
  }

  // 3) single order object
  if (isOrderObject(ordersPayload)) return [ordersPayload];

  return [];
}

function sortNewestFirst(list: OrderLike[]) {
  const out = [...list];
  out.sort((a, b) => {
    const da = new Date(pickOrderDate(a) || 0).getTime();
    const db = new Date(pickOrderDate(b) || 0).getTime();
    return (Number.isFinite(db) ? db : 0) - (Number.isFinite(da) ? da : 0);
  });
  return out;
}

// -----------------------------
// Pickers
// -----------------------------
function pickOrderId(o: any) {
  return String(
    o?.id ??
      o?.order_id ??
      o?.shopify_order_id ??
      o?.shopify_id ??
      o?.order_number ??
      o?.shopify_order_number ??
      ""
  );
}

function pickOrderNumber(o: any) {
  // Prefer Shopify order number if present
  return o?.shopify_order_number ?? o?.order_number ?? null;
}

function pickOrderDate(o: any) {
  return o?.processed_at || o?.created_at || o?.scheduled_at || null;
}

function pickTotal(o: any): string | null {
  const v = o?.total_price ?? o?.total ?? o?.price;
  if (v == null || v === "") return null;
  const n = Number(v);
  if (Number.isFinite(n)) return n.toFixed(2);
  return String(v);
}

function pickCurrency(o: any): string {
  return String(o?.currency || "GBP");
}

function pickStatus(o: any): string {
  // Prefer Recharge charge status if it exists (REFUNDED is important)
  return String(
    o?.charge_status || o?.status || o?.state || o?.financial_status || "UNKNOWN"
  );
}

// -----------------------------
// UI helpers
// -----------------------------
function statusChip(status?: string) {
  const raw = String(status || "UNKNOWN").trim();
  const s = raw.toLowerCase();

  const label =
    s === "success"
      ? "Processed"
      : s === "refunded"
      ? "Refunded"
      : s === "queued"
      ? "Queued"
      : s === "error"
      ? "Error"
      : s === "cancelled"
      ? "Cancelled"
      : s === "skipped"
      ? "Skipped"
      : raw;

  // Quiz-style colors
  const bgColor =
    s === "success"
      ? "#E5FFEA"
      : s === "refunded"
      ? "#FFF0D6"
      : s === "error"
      ? "#FFE5E2"
      : s === "cancelled" || s === "skipped"
      ? QUIZ.color.cream2
      : s === "queued"
      ? "#E6F0FF"
      : QUIZ.color.cream2;

  const textColor =
    s === "success"
      ? "#2E7D32"
      : s === "refunded"
      ? "#CE7A00"
      : s === "error"
      ? "#D14C3F"
      : s === "cancelled" || s === "skipped"
      ? "#666"
      : s === "queued"
      ? "#1A57D3"
      : "#666";

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        background: bgColor,
        color: textColor,
        fontWeight: 700,
        textTransform: "capitalize",
        height: 28,
        "& .MuiChip-label": { px: 1.5, fontSize: 12 },
      }}
    />
  );
}

function money(currency: string, amount?: string | null) {
  if (!amount) return "—";
  if (currency === "GBP") return `£${amount}`;
  return `${amount} ${currency}`;
}

// -----------------------------
// Page
// -----------------------------
export function OrderHistoryPage({
  orders,
  products = [],
  subscriptions = [],
}: {
  orders: any;
  products?: any[];
  subscriptions?: any[];
}) {
  const list = useMemo(() => normalizeOrders(orders), [orders]);

  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      <QuizStyledCard>
        <Box sx={{ mb: 3 }}>
          <PageTitle
            title="Order History"
            subtitle="View all your past orders and deliveries"
          />
        </Box>

        {list.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 4, md: 6 },
            }}
          >
            <Box
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                borderRadius: "50%",
                backgroundColor: QUIZ.color.cream2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <ShoppingBagIcon
                sx={{ fontSize: { xs: 40, md: 50 }, color: QUIZ.color.brown }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 22 },
                fontWeight: 800,
                color: QUIZ.color.brown,
                mb: 1,
              }}
            >
              No orders yet
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 14, md: 16 },
                color: QUIZ.color.brown,
                opacity: 0.75,
              }}
            >
              Your order history will appear here once you place your first order
            </Typography>
          </Box>
        ) : (
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            {list.map((o, idx) => {
              const id = pickOrderId(o) || `order-${idx}`;
              const orderNo = pickOrderNumber(o);
              const date = pickOrderDate(o);
              const total = pickTotal(o);
              const currency = pickCurrency(o);
              const status = pickStatus(o);

              const lineItemsCount = Array.isArray(o?.line_items)
                ? o.line_items.length
                : null;

              // Extract line items with images (pass subscriptions for bundle meal extraction)
              const lineItems = extractLineItemsWithImages(o, products, subscriptions);
              const grouped = groupLineItems(lineItems);

              return (
                <QuizStyledCard variant="white" key={id}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 18, md: 20 },
                          fontWeight: 800,
                          color: QUIZ.color.brown,
                          mb: 0.5,
                        }}
                      >
                        Order {orderNo != null ? `#${orderNo}` : ""}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: { xs: 13, md: 14 },
                          color: QUIZ.color.brown,
                          opacity: 0.65,
                        }}
                      >
                        {date ? fmtDate(date) : "—"} • {money(currency, total)}
                      </Typography>
                    </Box>

                    {statusChip(status)}
                  </Box>

                  {/* Line Items */}
                  {lineItems.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: { xs: 14, md: 15 },
                            fontWeight: 700,
                            color: QUIZ.color.brown,
                            mb: 1.5,
                          }}
                        >
                          Order Items
                        </Typography>

                        {/* Bundle Products with nested meals */}
                        {grouped.bundles.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: QUIZ.color.brown,
                                opacity: 0.5,
                                mb: 1,
                              }}
                            >
                              Meal Plans
                            </Typography>
                            <Stack spacing={1.5}>
                              {grouped.bundles.map((bundle, idx) => (
                                <Box
                                  key={`${bundle.variantId}-${idx}`}
                                  sx={{
                                    border: "1px solid rgba(0,0,0,.08)",
                                    borderRadius: "10px",
                                    overflow: "hidden",
                                    background: QUIZ.color.cream,
                                  }}
                                >
                                  {/* Bundle Header */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "center",
                                      p: 1.25,
                                      background: QUIZ.color.cream,
                                      borderBottom: bundle.subItems && bundle.subItems.length > 0
                                        ? "1px solid rgba(0,0,0,.08)"
                                        : "none",
                                    }}
                                  >
                                    {bundle.imageUrl && (
                                      <Box
                                        component="img"
                                        src={bundle.imageUrl}
                                        alt={bundle.productTitle}
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: "8px",
                                          objectFit: "cover",
                                          border: "1px solid rgba(0,0,0,.10)",
                                        }}
                                      />
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        sx={{ fontSize: 13, fontWeight: 700, color: QUIZ.color.brown }}
                                        noWrap
                                      >
                                        {bundle.productTitle}
                                      </Typography>
                                      {bundle.variantTitle && (
                                        <Typography
                                          sx={{ fontSize: 11, color: QUIZ.color.brown, opacity: 0.65 }}
                                          noWrap
                                        >
                                          {bundle.variantTitle}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Chip
                                      label={`×${bundle.quantity}`}
                                      size="small"
                                      sx={{
                                        background: "#E5FFEA",
                                        color: "#2E7D32",
                                        fontWeight: 700,
                                        height: 24,
                                        fontSize: 11,
                                      }}
                                    />
                                  </Box>

                                  {/* Nested Meals */}
                                  {bundle.subItems && bundle.subItems.length > 0 && (
                                    <Box sx={{ p: 1.25 }}>
                                      <Typography
                                        sx={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: QUIZ.color.brown,
                                          opacity: 0.5,
                                          mb: 0.75,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.5px",
                                        }}
                                      >
                                        Meals in this plan
                                      </Typography>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.75,
                                        }}
                                      >
                                        {bundle.subItems.map((meal, mealIdx) => (
                                          <Box
                                            key={`${meal.variantId}-${mealIdx}`}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.75,
                                              px: 1,
                                              py: 0.5,
                                              borderRadius: "6px",
                                              background: QUIZ.color.warmWhite,
                                              border: "1px solid rgba(0,0,0,.06)",
                                            }}
                                          >
                                            {meal.imageUrl && (
                                              <Box
                                                component="img"
                                                src={meal.imageUrl}
                                                alt={meal.productTitle}
                                                sx={{
                                                  width: 24,
                                                  height: 24,
                                                  borderRadius: "4px",
                                                  objectFit: "cover",
                                                }}
                                              />
                                            )}
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                fontWeight: 600,
                                                color: QUIZ.color.brown,
                                              }}
                                              noWrap
                                            >
                                              {meal.productTitle}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                fontSize: 10,
                                                color: "#2E7D32",
                                                fontWeight: 600,
                                              }}
                                            >
                                              ×{meal.quantity}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Individual Meals */}
                        {grouped.meals.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: QUIZ.color.brown,
                                opacity: 0.5,
                                mb: 1,
                              }}
                            >
                              Meals
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              {grouped.meals.map((item, idx) => (
                                <Box
                                  key={`${item.variantId}-${idx}`}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: "8px",
                                    background: QUIZ.color.cream,
                                    border: "1px solid rgba(0,0,0,.08)",
                                  }}
                                >
                                  {item.imageUrl && (
                                    <Box
                                      component="img"
                                      src={item.imageUrl}
                                      alt={item.productTitle}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "6px",
                                        objectFit: "cover",
                                      }}
                                    />
                                  )}
                                  <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: QUIZ.color.brown }}>
                                      {item.productTitle}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: QUIZ.color.brown, opacity: 0.65 }}>
                                      Qty: {item.quantity}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Treats */}
                        {grouped.treats.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: QUIZ.color.brown,
                                opacity: 0.5,
                                mb: 1,
                              }}
                            >
                              Treats & Add-ons
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              {grouped.treats.map((item, idx) => (
                                <Box
                                  key={`${item.variantId}-${idx}`}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: "8px",
                                    background: QUIZ.color.cream,
                                    border: "1px solid rgba(0,0,0,.08)",
                                  }}
                                >
                                  {item.imageUrl && (
                                    <Box
                                      component="img"
                                      src={item.imageUrl}
                                      alt={item.productTitle}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "6px",
                                        objectFit: "cover",
                                      }}
                                    />
                                  )}
                                  <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: QUIZ.color.brown }}>
                                      {item.productTitle}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: QUIZ.color.brown, opacity: 0.65 }}>
                                      Qty: {item.quantity}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* Other Items */}
                        {grouped.other.length > 0 && (
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: QUIZ.color.brown,
                                opacity: 0.5,
                                mb: 1,
                              }}
                            >
                              Other Items
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              {grouped.other.map((item, idx) => (
                                <Box
                                  key={`${item.variantId}-${idx}`}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: "8px",
                                    background: QUIZ.color.cream,
                                    border: "1px solid rgba(0,0,0,.08)",
                                  }}
                                >
                                  {item.imageUrl && (
                                    <Box
                                      component="img"
                                      src={item.imageUrl}
                                      alt={item.productTitle}
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "6px",
                                        objectFit: "cover",
                                      }}
                                    />
                                  )}
                                  <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: QUIZ.color.brown }}>
                                      {item.productTitle}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: QUIZ.color.brown, opacity: 0.65 }}>
                                      Qty: {item.quantity}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </QuizStyledCard>
              );
            })}
          </Stack>
        )}
      </QuizStyledCard>
    </Stack>
  );
}
